import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { t, formatEur } from "@/i18n/sk";
import { useCart } from "@/context/CartContext";
import { resolveImg } from "@/lib/api";

export function CartDrawer() {
  const { open, setOpen, items, updateQuantity, removeItem, validated, loadingValidation } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const subtotal = validated?.subtotal_eur ?? items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shipping = validated?.shipping_eur ?? 0;
  const total = validated?.total_eur ?? subtotal;
  const threshold = validated?.free_shipping_threshold_eur ?? 75;
  const remaining = Math.max(0, threshold - subtotal);
  const progress = Math.min(100, (subtotal / threshold) * 100);
  const hasAdjusted = validated?.items?.some((i) => i.adjusted);

  return (
    <div className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-vault-black/75 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className={`absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-white/10 bg-vault-midnight shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between border-b border-white/8 px-6 py-5">
          <h2 className="font-display text-lg font-bold text-white">{t.cart.title}</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Zavrieť" className="text-slate-300 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/8 bg-vault-card">
              <ShoppingBag className="h-7 w-7 text-slate-500" />
            </div>
            <p className="text-slate-400">{t.cart.empty}</p>
            <button onClick={() => setOpen(false)} className="btn-glow rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-2.5 text-sm font-semibold text-white">
              {t.actions.continueShopping}
            </button>
          </div>
        ) : (
          <>
            <div className="border-b border-white/8 px-6 py-4">
              <div className="mb-2 flex items-center gap-2 text-xs text-slate-300">
                <Truck className="h-4 w-4 text-cyan-300" />
                {remaining > 0 ? t.cart.freeShippingRemain(formatEur(remaining)) : t.cart.freeShipping}
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/8">
                <div className="h-full rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ul className="flex flex-col gap-4">
                {items.map((item) => (
                  <li key={item.product_id} data-testid={`cart-item-${item.slug}`} className="flex gap-4 rounded-xl border border-white/8 bg-vault-card p-3">
                    <Link to={`/produkt/${item.slug}`} onClick={() => setOpen(false)} className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/8 bg-vault-black/60 p-2">
                      {item.image && <img src={resolveImg(item.image)} alt={item.name} className="h-full w-full object-contain" />}
                    </Link>
                    <div className="flex flex-1 flex-col">
                      <span className="text-[0.65rem] uppercase tracking-wider text-cyan-300">{item.expansion_name}</span>
                      <Link to={`/produkt/${item.slug}`} onClick={() => setOpen(false)} className="line-clamp-2 text-sm font-semibold text-white hover:text-cyan-200">
                        {item.name}
                      </Link>
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2 rounded-full border border-white/10 p-1">
                          <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} data-testid={`cart-decrease-${item.slug}`} className="flex h-6 w-6 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-5 text-center text-sm font-semibold text-white">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} data-testid={`cart-increase-${item.slug}`} className="flex h-6 w-6 items-center justify-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="font-mono text-sm font-bold text-white">{formatEur(item.price * item.quantity)}</span>
                      </div>
                    </div>
                    <button onClick={() => removeItem(item.product_id)} data-testid={`cart-remove-${item.slug}`} aria-label={t.actions.remove} className="self-start text-slate-500 hover:text-rose-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
              {hasAdjusted && (
                <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                  {t.cart.adjustedNote}
                </p>
              )}
            </div>

            <div className="border-t border-white/8 px-6 py-5">
              <div className="mb-4 flex flex-col gap-1.5 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>{t.cart.subtotal}</span>
                  <span className="font-mono">{formatEur(subtotal)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>{t.cart.shipping}</span>
                  <span className="font-mono">{shipping === 0 ? t.cart.freeShipping : formatEur(shipping)}</span>
                </div>
                <div className="mt-1 flex justify-between border-t border-white/8 pt-2 text-base font-bold text-white">
                  <span>{t.cart.total}</span>
                  <span className="font-mono" data-testid="cart-total">{loadingValidation ? "…" : formatEur(total)}</span>
                </div>
              </div>

              <button
                data-testid="cart-drawer-checkout-button"
                onClick={() => { setOpen(false); navigate("/pokladna"); }}
                className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-3.5 text-sm font-semibold text-white"
              >
                {t.cart.goToCheckout}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default CartDrawer;
